import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import type { ChamadaLog } from '../../types';
import { useTenant } from '../../hooks/useTenant';

interface Props {
  aberto: boolean;
  onClose: () => void;
  data: string;
  indiceAula: number;
  grupoId: string;
  boExistente?: ChamadaLog | null;
}

const CANCELAMENTO_TIPOS = new Set([
  'Médico pessoal',
  'Médico trabalho',
  'Particular',
  'Reunião',
  'Secretaria',
  'Raios e Trovões',
  'Atestado / Afastamento',
]);

const TIPOS_PESSOAIS = [
  'Médico pessoal',
  'Médico trabalho',
  'Particular',
  'Reunião',
  'Secretaria',
  'Atestado / Afastamento',
];

const TIPOS_GERAIS = [
  'Manutenção/Incidente',
  'Raios e Trovões',
  'Reunião',
];

const CardBO: React.FC<Props> = ({ aberto, onClose, data, indiceAula, grupoId, boExistente }) => {
  const [isPessoal, setIsPessoal] = useState(true);
  const [comprometeDia, setComprometeDia] = useState(false);
  const [tipo, setTipo] = useState(TIPOS_PESSOAIS[0]);
  const [descricao, setDescricao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [cancelarAula, setCancelarAula] = useState(false);
  const [diasInput, setDiasInput] = useState('');
  const [erro, setErro] = useState('');
  const [extrapRestrita, setExtrapRestrita] = useState(false);

  const { tenantId } = useTenant();

  // Toggle 'extrapolacao_restrita' da unidade: aviso do via_1 muda de texto
  useEffect(() => {
    if (!aberto) return;
    api.get('/app-settings/extrapolacao_restrita')
      .then((res) => setExtrapRestrita(res.data?.value?.[tenantId] === true))
      .catch(() => setExtrapRestrita(false));
  }, [aberto, tenantId]);

  if (!aberto) return null;

  const handleTogglePessoal = (v: boolean) => {
    setIsPessoal(v);
    setTipo(v ? TIPOS_PESSOAIS[0] : TIPOS_GERAIS[0]);
    setCancelarAula(false);
    setErro('');
  };

  const tipos = isPessoal ? TIPOS_PESSOAIS : TIPOS_GERAIS;
  const isCancelamento = CANCELAMENTO_TIPOS.has(tipo) || (tipo === 'Manutenção/Incidente' && cancelarAula);
  const via = isPessoal ? 'via_2' : 'via_1';
  const diasQtd = tipo === 'Atestado / Afastamento' && diasInput ? parseInt(diasInput) : 0;
  const multiDia = isPessoal && diasQtd >= 1;

  const formatarData = (offset: number) => {
    const d = new Date(data + 'T12:00');
    d.setDate(d.getDate() + offset);
    return d.toLocaleDateString('pt-BR');
  };

  const handleSalvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      await api.post('/chamadas/card-bo', {
        data,
        indice_aula: indiceAula,
        via,
        tipo_ocorrencia: tipo,
        motivo: descricao,
        compromete_dia: comprometeDia || undefined,
        cancelar_aula: cancelarAula || undefined,
        grupo_id: grupoId,
        dias: multiDia ? diasQtd : undefined,
      });
      onClose();
    } catch (err: any) {
      console.error(err);
      setErro(err?.response?.data?.message || err?.response?.data?.error || 'Erro ao salvar o BO — tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const handleCancelarBO = async () => {
    if (!boExistente) return;
    try {
      await api.delete('/chamadas/card-bo', {
        data: { data, indice_aula: indiceAula, grupo_id: grupoId },
      });
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md shadow-xl dark:shadow-black/20 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Ocorrência / BO</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-xl">&times;</button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {new Date(data + 'T12:00').toLocaleDateString('pt-BR')} - Aula {indiceAula + 1}
        </p>

        {boExistente && (
          <div className="flex items-center gap-2 mb-4 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-xs">
            <span className="text-amber-700 dark:text-amber-400 truncate">
              BO: {boExistente.tipo_ocorrencia}{boExistente.motivo ? ` — ${boExistente.motivo}` : ''}
            </span>
            <button onClick={handleCancelarBO} title="Limpar BO"
              className="ml-auto shrink-0 text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-0.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="space-y-4">

          <div className="flex items-center gap-2">
            <input type="checkbox" id="pessoalCheck" checked={isPessoal}
              onChange={(e) => handleTogglePessoal(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded" />
            <label htmlFor="pessoalCheck" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Pessoal / Professor
            </label>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto">
              {via === 'via_2'
                ? 'Exclusivo do professor'
                : extrapRestrita ? 'Só as turmas deste professor' : 'Afeta todos os professores'}
            </span>
          </div>

          <div className={multiDia ? 'opacity-50' : ''}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Escopo</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input type="radio" name="compromete" checked={!comprometeDia}
                  onChange={() => setComprometeDia(false)}
                  disabled={multiDia}
                  className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600" />
                Compromete a aula
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input type="radio" name="compromete" checked={comprometeDia}
                  onChange={() => setComprometeDia(true)}
                  disabled={multiDia}
                  className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600" />
                Compromete o dia
              </label>
            </div>
            {multiDia && (
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                Ignorado com Qtd. dias preenchido — o afastamento cobre todas as turmas do professor.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo</label>
            <select value={tipo} onChange={(e) => { setTipo(e.target.value); setCancelarAula(false); setDiasInput(''); setErro(''); }}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded p-2 mt-1 text-sm">
              {tipos.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {isPessoal && tipo === 'Atestado / Afastamento' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Qtd. de dias (afastamento)</label>
              <input type="number" min="1" max="30" value={diasInput}
                onChange={(e) => setDiasInput(e.target.value)}
                placeholder="1"
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded p-2 mt-1 text-sm" />
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                Dias corridos a partir da data da ocorrência (apenas os dias de aula do label). Deixe vazio para cancelar apenas este dia.
              </p>
            </div>
          )}

          {tipo === 'Manutenção/Incidente' && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="cancelarAulaCheck" checked={cancelarAula}
                onChange={(e) => setCancelarAula(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded" />
              <label htmlFor="cancelarAulaCheck" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Cancelar aula na matriz
              </label>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto">
                Marque se a ocorrência cancela a aula
              </span>
            </div>
          )}

          {isCancelamento && (
            <div className="p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-xs text-red-600 dark:text-red-400 space-y-1">
              <p>Este tipo de ocorrência irá <strong>cancelar a aula</strong> na matriz de chamada.</p>
              {comprometeDia && !multiDia && <p>O cancelamento será aplicado em todas as aulas do dia.</p>}
              {multiDia && (
                <p>
                  Afastamento de <strong>{diasQtd} {diasQtd === 1 ? 'dia' : 'dias'}</strong> — de{' '}
                  <strong>{formatarData(0)}</strong> a <strong>{formatarData(diasQtd - 1)}</strong>: todas as
                  turmas do professor serão canceladas nesse período.
                </p>
              )}
              <p className="text-red-500 dark:text-red-400">
                {via === 'via_2'
                  ? 'Apenas as turmas deste professor serão afetadas (via_2).'
                  : extrapRestrita
                    ? 'Apenas as turmas deste professor serão afetadas (via_1, extrapolação restrita ativa).'
                    : 'Todas as turmas de todos os professores serão afetadas (via_1).'}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Descrição</label>
            <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)}
              rows={3} className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded p-2 mt-1 text-sm resize-none" />
          </div>
        </div>
        {erro && (
          <div className="mt-4 px-3 py-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-xs text-red-600 dark:text-red-400">
            {erro}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">Cancelar</button>
          <button onClick={handleSalvar} disabled={salvando}
            className="px-4 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-400 dark:disabled:bg-gray-600">
            {salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
};

export default CardBO;
